import { Injectable } from '@angular/core';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable, Subscription} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
/**
 * Abstracts the communication over HTTP
 */
export class HttpService {

  /**
   * @param http The HttpClient provided by angular
   */
  constructor(private http: HttpClient) { }

  /**
   * Make a POST request
   * @param url The target url
   * @param body The body of the request
   * @param printToConsole Whether or not the response and other info shall be printed onto the console
   */
  public post<responseType = Object, bodyType = Object>(url: string, body: bodyType, printToConsole?: boolean): Promise<HttpResponse<responseType>> {
    if (printToConsole) {
      this._printUpcomingRequestAction({method: HttpMethods.POST, url: url});
    }
    const headers: HttpHeaders = this._buildHeader();
    return this.http.post<responseType>(url, body, { headers: headers, observe: 'response'}).toPromise().then((res) => {
      return this._handleSuccessfulResponse(res, printToConsole);
    }).catch((error) => {
      return this._handleFailedResponse(error, HttpMethods.POST, printToConsole);
    });
  }

  /**
   * Make a GET request
   * @param url The target url
   * @param printToConsole Whether or not the response and other info shall be printed onto the console
   */
  public get(url: string, printToConsole?: boolean): Promise<any> {
    if (printToConsole) {
      this._printUpcomingRequestAction({method: HttpMethods.GET, url: url});
    }
    // const headers: HttpHeaders = this._buildHeader();
    // TODO: make all usages comply with observing the whole response rather than the content
    //  (such that using function can determine the http response code by itself)
    return this.http.get<string>(url, /*{observe: 'response'}*/).toPromise().then((res) => {
      return this._handleSuccessfulResponse(res, printToConsole);
    }).catch((error) => {
      return this._handleFailedResponse(error, HttpMethods.GET, printToConsole);
    });
  }

  /**
   * Make a DELETE request
   * @param url The target url
   */
  public delete(url: string): Promise<any> {
    this._printUpcomingRequestAction({method: HttpMethods.DELETE, url: url});
    const headers: HttpHeaders = this._buildHeader();
    return this.http.delete(url, { headers: headers}).toPromise().then((res) => {
      return this._handleSuccessfulResponse(res);
    }).catch((error) => {
      return this._handleFailedResponse(error, HttpMethods.DELETE);
    });
  }

  /**
   * Make a PATCH request
   * @param url The target url
   * @param body The request's body
   */
  public patch(url: string, body: Object): Promise<any> {
    this._printUpcomingRequestAction({method: HttpMethods.PATCH, url: url});
    const headers: HttpHeaders = this._buildHeader();
    return this.http.patch<string>(url, body, { headers: headers}).toPromise().then((res) => {
      return this._handleSuccessfulResponse(res);
    }).catch((error) => {
      return this._handleFailedResponse(error, HttpMethods.PATCH);
    });
  }

  /**
   * Handles a successful response
   * @param res The actual response object
   * @param printToConsole Whether or not the response and other info shall be printed onto the console
   * @private
   */
  private _handleSuccessfulResponse(res: any, printToConsole?: boolean): Promise<any> {
    // res = JSON.parse(res);
    if (printToConsole) {
      console.log('API responds: ', res);
    }
    // at least the request to register a new query does not have a response field, so no using it for check-up right now
    if (res) {
      return Promise.resolve(res);
    } else {
      return Promise.reject(res);
    }
  }

  /**
   * Handles a failed response
   * @param error The error that occurred
   * @param method The method that was used
   * @param printToConsole Whether or not the response and other info shall be printed onto the console
   * @private
   */
  private _handleFailedResponse(error: Error, method: HttpMethods, printToConsole?: boolean): Promise<Error> {
    if (error && error !== undefined && error != null) {
      if (printToConsole) {
        console.log(error);
      }
      return Promise.reject(error);
    } else {
      return Promise.reject(new Error(
        `This error was generated by the receiving ${method} function inside HttpRequestService which got a rejected promise with an UNDEFINED reason: ${error}`
      ));
    }
  }

  /**
   * Print the next request onto the console
   * @param info
   * @private
   */
  private _printUpcomingRequestAction(info: {method: HttpMethods, url: string}) {
    console.log(`Attempting to send HTTP ${info.method} to URL ${info.url}`);
  }

  /**
   * Build necessary headers
   * @private
   */
  private _buildHeader(): HttpHeaders {
    const headers: HttpHeaders = new HttpHeaders({
      // 'Access-Control-Allow-Origin': '*',
      'Content-Type': 'application/json'
    });
    // headers = this._authenticationService.addTokenToHeaders(headers);
    return headers;
  }

  /**
   * Converts an object into a query string, e.g. for GET requests
   * @param params
   * @param encode
   */
  public convertObjectToQueryString(params: Object, encode: boolean = true): string {
    if (encode) {
      return Object.keys(params).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key])).join('&');
    } else {
      return Object.keys(params).map(key => key + '=' + params[key]).join('&');
    }
  }
}

/**
 * An enum defining the possible HTTP methods
 */
export enum HttpMethods {
  GET = 'GET',
  POST = 'POST',
  DELETE = 'DELETE',
  PATCH = 'PATCH'
}
